import globalMessage from "@/utils/globalMessage";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Form, Input, Button, Spin, Modal, Row, Col } from "antd";
import DetailTabs from "@components/DetailTabs";
import { api } from "@redux/api";
import RecordAuditTimeline from "@components/Table/RecordAuditLog";
import { useSelector } from "react-redux";
import moment from "moment";

import CustomUserRelated from "@containers/management/Facility/CustomUserRelated/CustomUserRelated";
import AddressLookup from "@components/AddressLookup";
import CompanyLookup from "@containers/Lookup/CompanyLookup";
import CustomDatePicker from "@components/CustomDatePicker";
import { REGION_OPTIONS } from "@/constants/stateRegionMapping";
import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import "./index.css";
import FacilityMetrics from "@/components/facility-mertrics";
import Select from "@components/ResponsiveSelect";
import DatePicker from "@components/ResponsiveDatePicker";

const { TabPane } = DetailTabs;
const { TextArea } = Input;
const { Option } = Select;

const FacilityDetail = ({ isAdd = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { closeTab, refreshTab } = useKeepAliveContext();

  // Determine if this is a create page from route
  const isCreatePage = isAdd || location.pathname === '/admin/create-facility';

  const [loading, setLoading] = useState(isCreatePage ? false : true);
  const [readOnly, setReadOnly] = useState(!isCreatePage);
  const [facilityData, setFacilityData] = useState(null);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [facilityCategoryOptions, setFacilityCategoryOptions] = useState([]);
  const [activeTabKey, setActiveTabKey] = useState("1");

  const userName = useSelector(state => 
    state?.Auth?.user || 
    state?.Auth?.first_name + " " + state?.Auth?.last_name
  );

  useEffect(() => {
    if (isCreatePage) {
      fetchCompanyList();
      fetchFacilityCategoryList();
    } else if (id) {
      fetchFacilityDetail();
      fetchCompanyList();
      fetchFacilityCategoryList();
    }
  }, [id, isCreatePage]);

  const fetchFacilityDetail = async () => {
    try {
      setLoading(true);
      const response = await api.filterFacility(`facilityId=${id}`);

      if (response.status === 200 && response.data.results.length > 0) {
        const data = response.data.results[0];
        setFacilityData(data);
        // Set form values with date conversion
        const formValues = {
          ...data,
          year: data.year ? moment(data.year, "YYYY") : null,
          created_date: data.created_date ? moment(data.created_date).format("YYYY-MM-DD HH:mm") : "",
          modified_date: data.modified_date ? moment(data.modified_date).format("YYYY-MM-DD HH:mm") : "",
        };
        form.setFieldsValue(formValues);
      } else {
        message.error("Facility not found");
      }
    } catch (error) {
      console.error("Error fetching facility:", error);
      message.error("Failed to load facility details");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyList = async () => {
    try {
      const response = await api.filterCompany("page=1&page_size=999");
      if (response.status === 200) {
        setCompanyOptions(response.data.results || []);
      }
    } catch (error) {
      console.error("Error fetching company list:", error);
    }
  };

  const fetchFacilityCategoryList = async () => {
    try {
      const response = await api.getFacilityCategory();
      if (response.status === 200) {
        // 现在接口不再分页，直接返回数组
        const categories = Array.isArray(response.data) ? response.data : (response.data?.results || []);
        setFacilityCategoryOptions(categories);
      }
    } catch (error) {
      console.error("Error fetching facility category list:", error);
    }
  };

  const handleEdit = () => {
    setReadOnly(false);
  };

  const handleCancel = () => {
    setReadOnly(true);
    if (facilityData) {
      const formValues = {
        ...facilityData,
        year: facilityData.year ? moment(facilityData.year, "YYYY") : null,
        created_date: facilityData.created_date ? moment(facilityData.created_date).format("YYYY-MM-DD HH:mm") : "",
        modified_date: facilityData.modified_date ? moment(facilityData.modified_date).format("YYYY-MM-DD HH:mm") : "",
      };
      form.setFieldsValue(formValues);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Format dates and prepare values
      const submitValues = {
        ...values,
        year: values.year?.format ? values.year.format("YYYY") : values.year || null,
        // Ensure longitude and latitude are set
        longitude: values.longitude || 0,
        latitude: values.latitude || 0,
      };

      // Remove address array if it exists (as per Facility.js handleOnlineSave)
      if (Array.isArray(submitValues.address)) {
        delete submitValues.address;
      }

      let response;
      if (isCreatePage) {
        // Create mode
        response = await api.createFacility(submitValues);
      } else {
        // Edit mode
        submitValues.modifiedby_name = userName;
        response = await api.editFacility(id, submitValues);
      }

      if (response.status === 200 || response.status === 201) {
        globalMessage.success({
          message: "Success",
          description: isCreatePage ? "Facility created successfully" : "Facility updated successfully",
        });

        if (isCreatePage) {
          // Refresh Facility list page and close current tab
          refreshTab("/admin/facility");
          closeTab();
          navigate("/admin/facility");
        } else {
          setReadOnly(true);
          fetchFacilityDetail();
        }
      } else {
        globalMessage.error({
          message: "Error",
          description: isCreatePage ? "Failed to create facility" : "Failed to update facility",
        });
      }
    } catch (error) {
      console.error(`Error ${isCreatePage ? 'creating' : 'updating'} facility:`, error);
      globalMessage.error({
        message: "Error",
        description: isCreatePage ? "Failed to create facility" : "Failed to update facility",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Are you sure you want to delete this facility?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setLoading(true);
          const response = await api.deleteFacility(id);

          if (response.status === 204 || response.status === 200) {
            globalMessage.success({
              message: "Success",
              description: "Facility deleted successfully",
            });
            // Refresh Facility list page and close current tab
            refreshTab("/admin/facility");
            closeTab();
            navigate("/admin/facility");
          } else {
            globalMessage.error({
              message: "Error",
              description: "Failed to delete facility",
            });
          }
        } catch (error) {
          console.error("Error deleting facility:", error);
          globalMessage.error({
            message: "Error",
            description: "Failed to delete facility",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleCancelCreate = () => {
    closeTab();
    navigate("/admin/facility");
  };

  const renderOperationButton = () => {
    if (isCreatePage) {
      // Create mode - only show Add button
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button onClick={handleCancelCreate}>
            <i className="fa-solid fa-xmark pr-2"></i>Cancel
          </Button>
          <Button type="primary" onClick={() => form.submit()}>
            <i className="fa-solid fa-floppy-disk pr-2"></i>Save
          </Button>
        </div>
      );
    }

    if (readOnly) {
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="primary" onClick={handleEdit}>
            <i className="fa-solid fa-pen-to-square pr-2"></i>Edit
          </Button>
          <Button danger onClick={handleDelete}>
            <i className="fa-solid fa-trash pr-2"></i>Delete
          </Button>
        </div>
      );
    } else {
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="primary" onClick={() => form.submit()}>
            <i className="fa-solid fa-floppy-disk pr-2"></i>Save
          </Button>
          <Button onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      );
    }
  };

  // CustomUserRelated function
  const CustomUserRelatedComponent = () => {
    return <>{id ? <CustomUserRelated relatedId={id} /> : ''}</>
  };

  // Render form fields - reusable function for both create and edit modes
  const renderFormFields = () => {
    return (
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        className="detailForm"
                        autoComplete="off">
        <Row gutter={[0, 0]}>
          {/* ID - Hidden in create mode */}
          {!isCreatePage && (
            <Col xs={24} lg={12}>
              <Form.Item label="ID" name="id">
                <Input disabled />
              </Form.Item>
            </Col>
          )}

          {/* Name */}
          <Col xs={24} lg={12}>
            <Form.Item 
              label="Name" 
              name="name"
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Input 
                disabled={readOnly}
                autoComplete="off"
                allowClear
              />
            </Form.Item>
          </Col>

          {/* Category */}
          <Col xs={24} lg={12}>
            <Form.Item 
              label="Category" 
              name="facility_category"
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Select 
                disabled={readOnly}
                showSearch
                placeholder="Select facility category"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {facilityCategoryOptions.map(category => (
                  <Option key={category.id} value={category.id}>
                    {category.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* Company */}
          <Col xs={24} lg={12}>
            <Form.Item 
              label="Company" 
              name="company_name"
              rules={[{ required: true, message: "This field is required" }]}
            >
              {readOnly ? (
                <Input disabled value={facilityData?.company_name || ""} />
              ) : (
                <CompanyLookup formRef={form} record={facilityData?.company_name} />
              )}
            </Form.Item>
          </Col>

          {/* Company ID (hidden) */}
          <Col xs={24} lg={12} style={{ display: 'none' }}>
            <Form.Item name="company">
              <Input type="hidden" />
            </Form.Item>
          </Col>

          {/* Area */}
          <Col xs={24} lg={12}>
            <Form.Item 
              label="Area" 
              name="area"
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Input 
                disabled={readOnly}
                type="number"
                min="0"
                autoComplete="off"
                allowClear
              />
            </Form.Item>
          </Col>

          {/* Year */}
          <Col xs={24} lg={12}>
            <Form.Item 
              label="Year" 
              name="year"
              rules={[{ required: true, message: "This field is required" }]}
            >
              <CustomDatePicker 
                format="YYYY" 
                type="year" 
                style={{ width: "100%" }}
                disabled={readOnly}
              />
            </Form.Item>
          </Col>

          {/* Latitude (hidden) */}
          <Col xs={24} lg={12} style={{ display: 'none' }}>
            <Form.Item name="latitude">
              <Input />
            </Form.Item>
          </Col>

          {/* Longitude (hidden) */}
          <Col xs={24} lg={12} style={{ display: 'none' }}>
            <Form.Item name="longitude">
              <Input />
            </Form.Item>
          </Col>

          {/* Description - Full Width */}
          <Col xs={24} lg={12}>
            <Form.Item 
              label="Description" 
              name="description" 
            >
              <TextArea rows={3} disabled={readOnly} />
            </Form.Item>
          </Col>

          {/* Address - Full Width */}
          <Col xs={24} lg={12} style={{height: readOnly ? 48: 200}}>
            <Form.Item 
              label="Address" 
              name="address"
            >
              {readOnly ? (
                <Input disabled value={facilityData?.address || ""} />
              ) : (
                <AddressLookup formRef={form} value={facilityData?.address} fieldName="address" />
              )}
            </Form.Item>
          </Col>

          {/* City */}
          <Col xs={24} lg={12}>
            <Form.Item label="City" name="city">
              <Input placeholder="City" allowClear disabled />
            </Form.Item>
          </Col>

          {/* State */}
          <Col xs={24} lg={12}>
            <Form.Item label="State" name="state">
              <Input placeholder="State" allowClear disabled />
            </Form.Item>
          </Col>

          {/* Country */}
          <Col xs={24} lg={12}>
            <Form.Item label="Country" name="country">
              <Input placeholder="Country" allowClear disabled />
            </Form.Item>
          </Col>

          {/* Climate Zone */}
          <Col xs={24} lg={12}>
            <Form.Item 
              label="Climate Zone" 
              name="climate_zone"
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Select allowClear placeholder="Select climate zone" disabled={readOnly}>
                <Option value="1">1</Option>
                <Option value="2">2</Option>
                <Option value="3">3</Option>
                <Option value="4">4</Option>
                <Option value="5">5</Option>
                <Option value="6">6</Option>
                <Option value="7">7</Option>
                <Option value="8">8</Option>
              </Select>
            </Form.Item>
          </Col>

          {/* Region */}
          <Col xs={24} lg={12}>
            <Form.Item 
              label="Region" 
              name="region"
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Select allowClear placeholder="Select region (auto-filled from address)" disabled={readOnly}>
                {REGION_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* Station */}
          <Col xs={24} lg={12}>
            <Form.Item label="Station" name="station">
              <Input disabled={readOnly} />
            </Form.Item>
          </Col>

          {/* Created By - Hidden in create mode */}
          {!isCreatePage && (
            <Col xs={24} lg={12}>
              <Form.Item label="Created By" name="createdby_name">
                <span>{facilityData?.createdby_name}</span>
              </Form.Item>
            </Col>
          )}

          {/* Created Date - Hidden in create mode */}
          {!isCreatePage && (
            <Col xs={24} lg={12}>
              <Form.Item label="Created Date" name="created_date">
                <span>{facilityData?.created_date ? moment(facilityData.created_date).format("YYYY-MM-DD HH:mm") : ""}</span>
              </Form.Item>
            </Col>
          )}

          {/* Modified By - Hidden in create mode */}
          {!isCreatePage && (
            <Col xs={24} lg={12}>
              <Form.Item label="Modified By" name="lastModified_name">
                <span>{facilityData?.lastModified_name}</span>
              </Form.Item>
            </Col>
          )}

          {/* Modified Date - Hidden in create mode */}
          {!isCreatePage && (
            <Col xs={24} lg={12}>
              <Form.Item label="Modified Date" name="modified_date">
                <span>{facilityData?.modified_date ? moment(facilityData.modified_date).format("YYYY-MM-DD HH:mm") : ""}</span>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>
    );
  };

  if (loading && !facilityData && !isCreatePage) {
    return (
      <div style={{ textAlign: "center", padding: "100px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="facility-detail-container">
      <Spin spinning={loading}>
        <DetailTabs
          defaultActiveKey="1" 
          destroyInactiveTabPane={true}
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          tabBarExtraContent={activeTabKey === "1" ? renderOperationButton() : null}
        >
          <TabPane
            tab={
              <span>
                <i className="fa-regular fa-rectangle-list pr-2"></i>Detail
              </span>
            }
            key="1"
          >
            <div>
              <div className="slds-m-around_x-small">
                <div className="px-4">
                  <div className="w-full">
                    {renderFormFields()}
                  </div>
                </div>
              </div>
            </div>
          </TabPane>
          <TabPane
            tab={
              <span>
                <i className="fa-regular fa-rectangle-list pr-2"></i>Metrics
              </span>
            }
            key="2"
          >
            <div>
              <div className="slds-m-around_x-small">
                <div className="px-4">
                  <div className="w-full">
                  <FacilityMetrics record={facilityData} />
                  </div>
                </div>
              </div>
            </div>
          </TabPane>

          {/* Related and Log tabs - Hidden in create mode */}
          {!isCreatePage && (
            <>
              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-table-list pr-2"></i>Related
                  </span>
                }
                key="3"
              >
                <div className="px-4">
                  <CustomUserRelatedComponent />
                </div>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-timeline pr-2"></i>Log
                  </span>
                }
                key="4"
              >
                <div className="px-4">
                  <RecordAuditTimeline moduleId={id} module="Facility" />
                </div>
              </TabPane>
            </>
          )}
        </DetailTabs>
      </Spin>
    </div>
  );
};

export default FacilityDetail;

